/* Map — lifestyle places within 15 miles of the user's address (OpenStreetMap). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import AppHeader from "@/components/AppHeader";
import TimeLockDialog from "@/components/TimeLockDialog";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { MapPin, Search, Car, Sparkles, Scissors, Dice5, Plane, Paintbrush, KeyRound, Phone, Globe, Clock, X, Star, Lock, Trash2 } from "lucide-react";

const CATS = {
  car_dealership:      { label: "Car dealerships",      color: "rgb(var(--c-forest))", Icon: Car },
  used_car_dealership: { label: "Used car dealerships", color: "#2F7F8A", Icon: KeyRound },
  car_paint:           { label: "Car paint & body",     color: "rgb(var(--c-gold))", Icon: Paintbrush },
  spa:                 { label: "Spas",                 color: "#B85C8A", Icon: Sparkles },
  beauty_salon:        { label: "Beauty salons",        color: "#7B5EA7", Icon: Scissors },
  casino:              { label: "Casinos",              color: "rgb(var(--c-clay))", Icon: Dice5 },
  airport:             { label: "Airports",             color: "rgb(var(--c-ink))", Icon: Plane },
};
const iconCache = {};
const pinIcon = (cat) => {
  if (!iconCache[cat]) {
    const color = CATS[cat]?.color || "rgb(var(--c-moss))";
    iconCache[cat] = L.divIcon({
      className: "",
      html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid rgb(var(--c-cream));box-shadow:0 1px 4px rgba(26,37,32,.35)"></div>`,
      iconSize: [14, 14], iconAnchor: [7, 7], popupAnchor: [0, -8],
    });
  }
  return iconCache[cat];
};
const homeIcon = L.divIcon({
  className: "",
  html: `<div style="width:22px;height:22px;border-radius:50%;background:rgb(var(--c-cream));border:3px solid rgb(var(--c-ink));display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;border-radius:50%;background:rgb(var(--c-ink))"></div></div>`,
  iconSize: [22, 22], iconAnchor: [11, 11],
});

function Recenter({ center }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, 11); }, [center, map]);
  return null;
}

function PlaceRow({ p, onFocus, starred, onStar }) {
  const c = CATS[p.category];
  const tid = p.id.replace("/", "-");
  return (
    <div className="flex items-start gap-1 rounded-lg hover:bg-sand-light transition-colors" data-testid={`map-place-${tid}`}>
      <button onClick={() => onFocus(p)} className="flex-1 min-w-0 text-left px-3 py-2 flex items-start gap-2.5" data-testid={`map-place-focus-${tid}`}>
        <span className="mt-1 w-2.5 h-2.5 rounded-full shrink-0" style={{ background: c?.color }} />
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-ink truncate">{p.name}</span>
          <span className="block text-[10px] text-sage truncate">{c?.label} · {p.distance_miles} mi{p.address ? ` · ${p.address}` : ""}</span>
        </span>
      </button>
      <button onClick={() => onStar(p)} title={starred ? "Remove from wishlist" : "Star it — save to wishlist"}
        className={`px-2 py-2 shrink-0 ${starred ? "text-gold" : "text-[#C8C2B8] hover:text-gold"}`} data-testid={`map-star-${tid}`}>
        <Star className="w-4 h-4" strokeWidth={2} fill={starred ? "rgb(var(--c-gold))" : "none"} />
      </button>
    </div>
  );
}

function WishlistPanel({ items, onFocus, onRemove, onTimeLock }) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-2xl border border-gold/40 bg-gold/10 p-4 mt-4" data-testid="map-wishlist">
      <div className="flex items-center justify-between">
        <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-semibold text-gold"><Star className="w-3 h-3" fill="rgb(var(--c-gold))" /> Wishlist · {items.length}</div>
        <div className="text-[10px] text-sage">Star a place, then seal money toward it</div>
      </div>
      <ul className="mt-3 grid md:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((w) => (
          <li key={w.id} className="bg-cream border border-sand rounded-xl p-3 flex flex-col gap-2" data-testid={`wishlist-item-${w.id}`}>
            <button onClick={() => onFocus(w)} className="text-left min-w-0">
              <div className="text-sm text-ink truncate">{w.name}</div>
              <div className="text-[10px] text-sage truncate">{CATS[w.category]?.label}{w.address ? ` · ${w.address}` : ""}</div>
            </button>
            {w.timelock ? (
              <div className="text-[11px] inline-flex items-center gap-1.5 text-forest" data-testid={`wishlist-timelock-${w.id}`}>
                <Lock className="w-3 h-3" /> {fmtMoney(w.timelock.amount)} sealed · {w.timelock.status === "WITHDRAWN" ? "released" : `unlocks ${new Date(w.timelock.unlock_date).toLocaleDateString()}`}
              </div>
            ) : (
              <button onClick={() => onTimeLock(w)} className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-2.5 py-1.5 text-[11px] font-medium hover:bg-forest-deep self-start" data-testid={`wishlist-timelock-btn-${w.id}`}>
                <Lock className="w-3 h-3" /> Start a Time Lock for this
              </button>
            )}
            <button onClick={() => onRemove(w)} className="self-end text-sage hover:text-clay" title="Remove" data-testid={`wishlist-remove-${w.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function MapPage() {
  const [address, setAddress] = useState("");
  const [center, setCenter] = useState(null);
  const [data, setData] = useState(null);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [active, setActive] = useState(() => new Set(Object.keys(CATS)));
  const [focus, setFocus] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [tlFor, setTlFor] = useState(null);
  const pollRef = useRef(null);

  const loadWishlist = useCallback(async () => {
    try { const { data: d } = await api.get("/wishlist"); setWishlist(d.items || []); } catch { /* silent */ }
  }, []);
  useEffect(() => { loadWishlist(); }, [loadWishlist]);
  const starredIds = useMemo(() => new Set(wishlist.map((w) => w.place_id)), [wishlist]);
  const toggleStar = async (p) => {
    try {
      await api.post("/wishlist", { place_id: p.id, name: p.name, category: p.category, lat: p.lat, lng: p.lng, address: p.address || null });
      loadWishlist();
    } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
  };
  const removeWish = async (w) => { try { await api.delete(`/wishlist/${w.id}`); loadWishlist(); } catch { /* silent */ } };
  const onTimeLockDone = async (created) => {
    if (tlFor && created?.id) { try { await api.patch(`/wishlist/${tlFor.id}`, { timelock_id: created.id }); } catch { /* silent */ } }
    setTlFor(null); loadWishlist();
  };

  const stopPoll = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollNearby = useCallback((lat, lng) => {
    stopPoll();
    pollRef.current = setInterval(async () => {
      try {
        const { data: d } = await api.post("/places/nearby", { lat, lng });
        setData((prev) => ({ ...d, radius_m: d.radius_m || prev?.radius_m }));
        if (!d.pending) { setPending(false); stopPoll(); }
      } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); setPending(false); stopPoll(); }
    }, 4000);
  }, []);

  const runSearch = useCallback(async (addr) => {
    setLoading(true); setError(""); setFocus(null);
    try {
      const { data: d } = await api.post("/places/search", { address: addr, remember: true });
      setCenter(d.center); setData(d); setPending(!!d.pending);
      if (d.pending) pollNearby(d.center.lat, d.center.lng);
    } catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [pollNearby]);

  useEffect(() => {
    api.get("/places/saved").then(({ data: d }) => {
      if (d.saved?.query) { setAddress(d.saved.query); runSearch(d.saved.query); }
    }).catch(() => {});
    return stopPoll;
  }, [runSearch]);

  const places = useMemo(() => (data?.places || []).filter((p) => active.has(p.category)), [data, active]);
  const toggle = (k) => setActive((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const mapCenter = center ? [center.lat, center.lng] : [39.5, -98.35];
  const focusCenter = focus ? [focus.lat, focus.lng] : null;

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10" data-testid="map-page">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Map · Lifestyle</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">Your city, levelled up.</h1>
        <p className="text-moss mt-3 max-w-2xl leading-relaxed">
          Drop in your address and we&apos;ll light up the car dealerships, used-car lots, paint &amp; body shops, spas, salons, casinos and airports within {data?.radius_miles || 15} miles.
        </p>

        <form onSubmit={(e) => { e.preventDefault(); if (address.trim().length >= 3) runSearch(address.trim()); }}
              className="mt-6 flex gap-2 flex-wrap" data-testid="map-search-form">
          <div className="flex-1 min-w-[260px] relative">
            <MapPin className="w-4 h-4 text-sage absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, Las Vegas, NV"
              className="w-full bg-white border border-sand rounded-lg pl-9 pr-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent"
              data-testid="map-address-input" />
          </div>
          <button type="submit" disabled={loading || address.trim().length < 3}
            className="bg-forest text-white rounded-lg px-5 py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-50 inline-flex items-center gap-2 transition-colors"
            data-testid="map-search-btn">
            <Search className="w-4 h-4" strokeWidth={2} /> {loading ? "Finding…" : "Show my map"}
          </button>
        </form>
        {error && <div className="mt-3 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="map-error">{error}</div>}
        {center && (
          <div className="mt-2 text-xs text-moss" data-testid="map-center-label">
            Centered on <span className="font-medium text-ink">{center.display_name}</span>
          </div>
        )}

        {data && (
          <div className="mt-4 flex gap-2 flex-wrap" data-testid="map-filters">
            {Object.entries(CATS).map(([k, c]) => {
              const on = active.has(k); const n = data.counts?.[k] ?? 0;
              return (
                <button key={k} onClick={() => toggle(k)}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${on ? "text-white" : "bg-white text-moss border-sand"}`}
                  style={on ? { background: c.color, borderColor: c.color } : {}}
                  data-testid={`map-filter-${k}`}>
                  <c.Icon className="w-3.5 h-3.5" strokeWidth={2} /> {c.label}
                  <span className={`font-mono text-[10px] px-1.5 rounded-full ${on ? "bg-white/20" : "bg-sand-light"}`} data-testid={`map-count-${k}`}>{n}</span>
                </button>
              );
            })}
          </div>
        )}

        {pending && (
          <div className="mt-3 text-xs text-gold inline-flex items-center gap-2" data-testid="map-pending">
            <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
            Scanning 15 miles around you{data?.progress ? ` — ${data.progress.done} of ${data.progress.total} categories in` : ""}. Pins appear as each category lands (up to ~90s the first time).
          </div>
        )}

        <div className="mt-5 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-sand bg-white" style={{ height: 560 }} data-testid="map-canvas">
            <MapContainer center={mapCenter} zoom={center ? 11 : 4} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
              <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' />
              <Recenter center={focusCenter || (center ? mapCenter : null)} />
              {center && <>
                <Circle center={mapCenter} radius={data?.radius_m || 24140} pathOptions={{ color: "rgb(var(--c-forest))", weight: 1, fillOpacity: 0.04 }} />
                <Marker position={mapCenter} icon={homeIcon}><Popup>{center.display_name}</Popup></Marker>
              </>}
              {places.map((p) => (
                <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(p.category)}>
                  <Popup>
                    <div className="text-sm">
                      <div className="font-semibold">{p.name}</div>
                      <div className="text-xs text-moss">{CATS[p.category]?.label} · {p.distance_miles} mi</div>
                      {p.address && <div className="text-xs mt-1">{p.address}</div>}
                      {p.opening_hours && <div className="text-xs mt-1 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{p.opening_hours}</div>}
                      <div className="flex gap-3 mt-1.5 text-xs">
                        {p.phone && <a href={`tel:${p.phone}`} className="inline-flex items-center gap-1 text-forest"><Phone className="w-3 h-3" />{p.phone}</a>}
                        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-forest"><Globe className="w-3 h-3" />Website</a>}
                      </div>
                      <button onClick={() => toggleStar(p)} className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-gold" data-testid={`map-popup-star-${p.id.replace("/", "-")}`}>
                        <Star className="w-3 h-3" fill={starredIds.has(p.id) ? "rgb(var(--c-gold))" : "none"} /> {starredIds.has(p.id) ? "On your wishlist" : "Star it"}
                      </button>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          <aside className="rounded-2xl border border-sand bg-linen p-3 flex flex-col" style={{ maxHeight: 560 }} data-testid="map-list">
            <div className="flex items-center justify-between px-2 pb-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Nearby · {places.length}</div>
              {focus && <button onClick={() => setFocus(null)} className="text-[10px] text-moss inline-flex items-center gap-1" data-testid="map-clear-focus"><X className="w-3 h-3" /> back to center</button>}
            </div>
            <div className="overflow-y-auto flex-1 space-y-0.5">
              {!data && !loading && <div className="text-sm text-sage px-3 py-6 text-center" data-testid="map-empty">Enter your address to light up the map.</div>}
              {data && places.length === 0 && !pending && <div className="text-sm text-sage px-3 py-6 text-center" data-testid="map-no-results">Nothing in these categories nearby. Try toggling filters.</div>}
              {places.slice(0, 200).map((p) => <PlaceRow key={p.id} p={p} onFocus={setFocus} starred={starredIds.has(p.id)} onStar={toggleStar} />)}
            </div>
            <div className="text-[9px] text-sage px-2 pt-2 border-t border-sand mt-2">Places data © OpenStreetMap contributors · 15-mile radius · refreshed every 6 hours</div>
          </aside>
        </div>

        <WishlistPanel items={wishlist} onFocus={(w) => setFocus({ lat: w.lat, lng: w.lng })} onRemove={removeWish} onTimeLock={setTlFor} />
        {tlFor && <TimeLockDialog initialLabel={tlFor.name.slice(0, 60)} context={`Wishlist · ${CATS[tlFor.category]?.label || ""}${tlFor.address ? ` · ${tlFor.address}` : ""}`} onClose={() => setTlFor(null)} onDone={onTimeLockDone} />}
      </div>
    </div>
  );
}
